import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CONFIG } from '../config/config';
import { CalendarDays, Loader2, RotateCcw, Printer } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toISODateLocal } from '../utils/dateHelpers';

export default function AdminRapportBiQuotidienPanel({ etablissementId: etablissementIdProp } = {}) {
  const API = (CONFIG.API_URL || '/api').replace(/\/+$/, '');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const { etablissement } = useUser();
  const resolvedEtablissementId = etablissementIdProp ?? etablissement?.etablissement_id ?? null;
  const todayLocal = toISODateLocal(new Date());
  const [repDate, setRepDate] = useState(() => todayLocal);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: token ? `Bearer ${token}` : '' }), [token]);
  const printRef = useRef();

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const newWindow = window.open('', '_blank', 'width=800,height=600');
    newWindow.document.write(`
      <html>
        <head>
          <title>Revue facturation journée</title>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    newWindow.document.close();
    newWindow.focus();
    newWindow.print();
    newWindow.close();
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const etabParam = resolvedEtablissementId
        ? `&etablissement_id=${encodeURIComponent(resolvedEtablissementId)}`
        : '';
      const url = `${API}/exports/rapport-biquotidien?repDate=${repDate}${etabParam}`;
      const r = await fetch(url, { headers });
      if (r.status === 404) {
        setReport([]);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();

      if (!Array.isArray(data)) {
        throw new Error('Error fetching data');
      }
      setReport(data);
    } catch (e) {
      console.log(e.message);
      setReport([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [repDate, resolvedEtablissementId]);

  function formatEuro(val) {
    let num = parseFloat(val);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  }

  let runningTotal = 0;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-indigo-50 to-sky-50 px-4 py-3">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto] items-center gap-3">
            <LabeledDate label="Date" value={repDate} onChange={setRepDate} />
            <button
              onClick={handlePrint}
              className="justify-self-end inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed "
              title="Imprimer"
              disabled={report.length === 0}
            >
              <Printer className="w-4 h-4" />
              Imprimer
            </button>
            <button
              onClick={loadReport}
              className="justify-self-end inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50"
              title="Recharger"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              Recharger
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : report.length === 0 ? (
          <div className="italic text-slate-500">Aucune sortie sur la période.</div>
        ) : (
          <div ref={printRef}>
            <div className={'report-printable'}>
              <style>
                {`
                  .report-printable {
                    font-family: 'Inter', -apple-system, sans-serif;
                    background-color: #f0f2f5;
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                    padding: 10px;
                    margin: 0;
                  }
                  
                  .report-printable .container {
                    max-width: 1550px;
                    margin: auto;
                    background: white;
                    padding: 15px;
                    border-radius: 8px;
                  }
                  
                  .report-printable h2 {
                    color: #1a73e8;
                    margin-bottom: 15px;
                    font-weight: 500;
                  }
                  
                  .report-printable table {
                    width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                    border: 1px solid #dee2e6;
                  }
                  
                  .report-printable th {
                    background-color: #f8f9fa;
                    color: #5f6368;
                    text-align: left;
                    padding: 8px;
                    text-transform: uppercase;
                    font-size: 10px;
                    border: 2px solid #dee2e6;
                  }
                  
                  .report-printable .group-header {
                    text-align: center;
                    font-weight: bold;
                    color: #1a73e8;
                    background-color: #eef4ff;
                  }
                  
                  .report-printable td {
                    padding: 8px;
                    border: 2px solid #eee;
                    color: #3c4043;
                    font-size: 12px;
                    vertical-align: top;
                  }
                  
                  .report-printable tbody tr.patient-row:nth-child(4n+1),
                  .report-printable tbody tr.patient-row:nth-child(4n+1) + tr.cumul-row { background-color: #ffffff; }
                  .report-printable tbody tr.patient-row:nth-child(4n+3),
                  .report-printable tbody tr.patient-row:nth-child(4n+3) + tr.cumul-row { background-color: #fafafa; }
                  
                  .report-printable .col-date { width: 10%; }
                  .report-printable .col-patient { width: 10%; }
                  .report-printable .col-cotation { width: 15%; }
                  .report-printable .col-chiffrage { width: 20%; }
                  .report-printable .col-tp-ss { width: 15%; }
                  .report-printable .col-assurance { width: 15%; }
                  .report-printable .col-paiement { width: 15%; }
                  
                  .report-printable .patient-info {
                    font-weight: 600;
                    line-height: 1.3;
                    word-break: break-word;
                    font-size: 11px;
                  }
                  
                  .report-printable .badge {
                    display: inline-block;
                    background: #e8f0fe;
                    padding: 3px 6px;
                    border-radius: 4px;
                    font-size: 9px;
                    font-weight: bold;
                    margin-bottom: 3px;
                    border: 1px solid #d2e3fc;
                    white-space: normal;
                    line-height: 1.2;
                    width: 90%;
                  }
                  
                  .report-printable .amount-row {
                    font-family: 'Roboto Mono', monospace;
                    text-align: right;
                  }
                  
                  .report-printable .total-bold {
                    font-weight: bold;
                    font-size: 15px;
                    color: #202124;
                  }
                  
                  .report-printable .manual-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 5px;
                    font-size: 11px;
                    font-weight: 600;
                    gap: 5px;
                  }
                  
                  .report-printable .box {
                    width: 45px;
                    height: 18px;
                    border: 1px solid #444;
                    background: white;
                    flex-shrink: 0;
                  }
                  
                  .report-printable .label-text {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  }
                  
                  .large-box {
                    width: 101px;
                    height: 18px;
                    border: 1px solid #444;
                    background: white;
                    margin-top: 2px;
                  }

                  .report-printable .page-footer {
                    display: block !important;
                    position: fixed;
                    bottom: -0.5cm;
                    right: 0;
                    width: 100%;
                    text-align: right;
                    font-size: 9px;
                    color: #999;
                    border-top: 1px solid #eee;
                    padding-top: 5px;
                  }
                  
                  .page-footer:after {
                    counter-increment: page;
                    content: "Document généré le " attr(data-date) " — Page " counter(page);
                  }
                  
                  .check-group {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                  }
                  
                  .check-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    font-size: 10px;
                    font-weight: bold;
                  }
                   
                  .small-box {
                    width: 18px;
                    height: 18px;
                    border: 1px solid #444;
                    background: white;
                    margin-top: 2px;
                  }
                  
                  .cumul-bar {
                    background: #eef4ff;
                    font-weight: bold;
                    font-size: 11px;
                    padding: 4px 10px;
                    border-radius: 4px;
                    text-align: center;
                    border: 1px solid #d2e3fc;
                  }
                  
                  @media print {
                    @page {
                      size: portrait;
                      margin: 1cm;
                    }
                    
                    html,
                    body {
                      height: auto !important;
                      overflow: visible !important;
                    }
                  
                    .report-printable .container {
                      box-shadow: none;
                      border: none;
                      width: 100% !important;
                      max-width: none !important;
                      padding: 0 !important;
                    }
                  
                    .report-printable thead {
                      display: table-header-group !important;
                    }
                  
                    .report-printable tr {
                      page-break-inside: avoid !important;
                    }
                    
                    .report-printable .no-print {
                      display: none !important;
                    }
                    
                    .report-printable .box, .report-printable .small-box {
                      border: 1.5px solid black !important;
                    }
                    
                    .report-printable th {
                      background-color: #f1f1f1 !important;
                      print-color-adjust: exact;
                      -webkit-print-color-adjust: exact;
                    }
                    
                    .report-printable .badge, .report-printable .cumul-bar {
                      print-color-adjust: exact;
                      -webkit-print-color-adjust: exact;
                    }
                  } 
                `}
              </style>
              <div className="container">
                <h2 className="no-print">Revue bi-quotidienne des facturations</h2>
                <table>
                  <thead>
                    <tr>
                      <th rowSpan="2" className="col-date">
                        Date / Heures
                      </th>
                      <th rowSpan="2" className="col-patient">
                        Patient
                      </th>
                      <th colSpan="2" className="group-header">
                        Chiffre d'affaire
                      </th>
                      <th colSpan="3" className="group-header">
                        Paiement & Tiers Payant
                      </th>
                    </tr>
                    <tr>
                      <th className="col-cotation">Cotation</th>
                      <th className="col-chiffrage" style={{ textAlign: 'right' }}>
                        Chiffrage (€)
                      </th>
                      <th className="col-paiement">Paiement(s)</th>
                      <th className="col-tp-ss">Tiers Payant SS</th>
                      <th className="col-assurance">Assurance / Autre</th>
                    </tr>
                  </thead>

                  <tbody>
                    {report.map((row) => {
                      runningTotal += parseFloat(row.total) || 0;
                      return (
                        <>
                          <tr className="patient-row" key={`bi-quot-${row.prid}`}>
                            <td className="col-date">
                              <strong>
                                {new Date(row.j_in).toISOString().slice(0, 10).replace(/-/g, '/')}
                              </strong>
                              <br />
                              <span style={{ color: '#666', fontSize: '10px' }}>
                                In: {row.h_in}
                              </span>
                              <br />
                              <span style={{ color: '#666', fontSize: '10px' }}>
                                Out: {row.sortie}
                              </span>
                            </td>

                            <td className="col-patient patient-info">
                              {row.is_anonymized ? (
                                <>
                                  <span style={{ fontFamily: 'monospace', fontSize: '10px', color: '#888', letterSpacing: '0.5px' }}>
                                    {row.details_patient?.split('\n')[0] || '—'}
                                  </span>
                                  <br />
                                  <span style={{ fontSize: '10px', color: '#555', fontWeight: 600 }}>
                                    {row.details_patient?.split('\n')[1] || ''}
                                  </span>
                                  <br />
                                  <span style={{ fontSize: '9px', color: '#aaa', fontStyle: 'italic' }}>anonymisé</span>
                                </>
                              ) : (
                                row.details_patient?.split('\n').map((line, i) => (
                                  <span key={i}>
                                    {i === 0 ? <strong>{line}</strong> : <span style={{ fontSize: '10px', color: '#666' }}>{line}</span>}
                                    {i === 0 && <br />}
                                  </span>
                                ))
                              )}
                            </td>

                            <td className="col-cotation">
                              <span className="badge">
                                Docteur:
                                <br />
                                {row.cotation_docteur
                                  ? row.cotation_docteur.toString().replace(/\+/g, ' + ')
                                  : '--'}
                              </span>
                              <br />
                              <span className="badge">
                                Chiffrage:
                                <br />
                                {row['cotation_chiffrée']
                                  ? row['cotation_chiffrée'].toString().replace(/\+/g, ' + ')
                                  : '--'}
                              </span>
                            </td>

                            <td className="col-chiffrage amount-row">
                              <div className="total-bold">{formatEuro(row.total)}</div>
                              <div style={{ fontSize: '11px' }}>BRSS: {formatEuro(row.brss)}</div>
                              <div style={{ fontSize: '11px' }}>
                                Dépt: {formatEuro(row['dépassement'])}
                              </div>
                            </td>

                            <td rowspan="2" className="col-paiement">
                              <div className="manual-row">
                                <span className="label-text">TPE</span> <div className="box"></div>
                              </div>
                              <div className="manual-row">
                                <span className="label-text">Cash</span> <div className="box"></div>
                              </div>
                              <div className="manual-row">
                                <span className="label-text">Vir.</span> <div className="box"></div>
                              </div>
                            </td>

                            <td rowspan="2" className="col-tp-ss">
                              <div className="manual-row">
                                <span className="label-text">Montant</span>{' '}
                                <div className="box"></div>
                              </div>
                              <div className="manual-row">
                                <span className="label-text">Motif TP</span>{' '}
                                <div className="box"></div>
                              </div>
                            </td>

                            <td rowspan="2" className="col-assurance">
                              <div className="manual-row">
                                <span className="label-text">Montant</span>{' '}
                                <div className="box"></div>
                              </div>
                              <div className="manual-row">
                                <span className="label-text">Assur.</span>{' '}
                                <div className="box"></div>
                              </div>
                              <div className="manual-row">
                                <span className="label-text">Autre</span>{' '}
                                <div className="box"></div>
                              </div>
                            </td>
                          </tr>

                          <tr className="cumul-row">
                            <td colSpan="2" style={{ borderBottom: '2px solid #dee2e6' }}>
                              <div className="cumul-bar">
                                Cumul CA : {formatEuro(runningTotal)} €
                              </div>
                            </td>

                            <td colSpan="2" style={{ borderBottom: '2px solid #dee2e6' }}>
                              <div className="check-group">
                                <div className="check-item">
                                  <span>Doctolib, si différent</span>
                                  <div className="large-box"></div>
                                </div>
                                <div className="check-item">
                                  <span>FSE</span>
                                  <div className="small-box"></div>
                                </div>
                                <div className="check-item">
                                  <span>FSP</span>
                                  <div className="small-box"></div>
                                </div>
                                <div className="check-item">
                                  <span>Hono</span>
                                  <div className="small-box"></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LabeledDate({ label, value, onChange }) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs font-semibold text-slate-600">{label}</span>
      <div className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
        <CalendarDays className="w-4 h-4 text-slate-500" />
        <input
          type="date"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className="px-1 py-1 outline-none"
        />
      </div>
    </label>
  );
}